// SS7.2.5 F5 — atajo "reservar como alumno"
//
// Pantalla con grid de instancias donde el profesor puede reservar:
//   - Excluye sus propias clases (titular).
//   - Excluye instancias donde ya tiene reserva activa.
//   - Aplica descuento del catálogo de su perfil.
//   - Reserva queda en estado provisional_profesor; cede plaza si la clase
//     se llena con alumnos (FIFO con email automático de aviso).

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, Loader2, Calendar, MapPin, Users,
} from 'lucide-react';

import { getProfesorClasesDisponibles } from '../../lib/profesorApi';
import { postReserva } from '../../lib/api';
import { ApiError } from '../../lib/api';

const TOKEN_KEY = 'idoyoga_auth_token';
function token() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

function formatFechaLarga(iso, lang) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const locales = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  const f = new Intl.DateTimeFormat(locales[lang] || 'es-ES', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  const s = f.format(date);
  return s.charAt(0).toLocaleUpperCase(locales[lang]) + s.slice(1);
}

function hoyISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDiasISO(iso, dias) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + dias);
  return date.toISOString().slice(0, 10);
}

export default function ProfesorReservarComoAlumno() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || 'es';

  const [desde, setDesde] = useState(hoyISO());
  const [hasta, setHasta] = useState(addDiasISO(hoyISO(), 30));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [reservandoKey, setReservandoKey] = useState(null);
  const [bannerOk, setBannerOk] = useState(null);
  const [bannerErr, setBannerErr] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    setError(null);
    getProfesorClasesDisponibles({ desde, hasta, idioma: lang })
      .then((r) => setData(r))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [desde, hasta, lang]);

  useEffect(() => { cargar(); }, [cargar]);

  // Auto-clear banners
  useEffect(() => {
    if (!bannerOk) return;
    const id = setTimeout(() => setBannerOk(null), 4000);
    return () => clearTimeout(id);
  }, [bannerOk]);

  const reservar = async (item) => {
    const key = `${item.horario_id}|${item.fecha_clase}`;
    setReservandoKey(key);
    setBannerErr(null);
    try {
      const r = await postReserva(
        {
          horario_id: item.horario_id,
          fecha_clase: item.fecha_clase,
          metodo_pago: 'en_estudio',
        },
        token(),
        lang,
      );
      setBannerOk({
        codigo: r.codigo_reserva,
        clase: item.clase_tipo?.nombre || '',
      });
      // refrescar grid (la instancia ya no aparecerá)
      cargar();
    } catch (e) {
      const code = (e instanceof ApiError && e.code) || 'GENERICO';
      setBannerErr({ code, msg: e.message });
    } finally {
      setReservandoKey(null);
    }
  };

  const presetSemana = () => {
    setDesde(hoyISO());
    setHasta(addDiasISO(hoyISO(), 7));
  };
  const presetMes = () => {
    setDesde(hoyISO());
    setHasta(addDiasISO(hoyISO(), 30));
  };

  const items = data?.data || [];

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1>{t('profesor.reservar.titulo')}</h1>
        <p className="admin-page-sub">{t('profesor.reservar.sub')}</p>
      </header>

      <section className="profesor-reservar-aviso">
        <AlertCircle size={18} strokeWidth={1.5} />
        <span>{t('profesor.reservar.aviso_provisional')}</span>
      </section>

      <section className="admin-section">
        <div className="profesor-reservar-filtros">
          <div className="profesor-reservar-rango">
            <label>
              <span>{t('profesor.reservar.filtro_desde')}</span>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
            </label>
            <label>
              <span>{t('profesor.reservar.filtro_hasta')}</span>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
            </label>
          </div>
          <div className="profesor-reservar-presets">
            <button type="button" className="btn-mini" onClick={presetSemana}>
              {t('profesor.reservar.preset_semana')}
            </button>
            <button type="button" className="btn-mini" onClick={presetMes}>
              {t('profesor.reservar.preset_mes')}
            </button>
          </div>
        </div>

        {bannerOk && (
          <div className="admin-msg-ok profesor-reservar-banner">
            {t('profesor.reservar.banner_ok', { codigo: bannerOk.codigo, clase: bannerOk.clase })}
          </div>
        )}
        {bannerErr && (
          <div className="modal-error-general profesor-reservar-banner">
            <AlertCircle size={16} />
            <span>{t(`profesor.reservar.errores.${bannerErr.code}`, t('profesor.reservar.errores.GENERICO'))}</span>
          </div>
        )}

        {loading && (
          <div className="admin-loading"><Loader2 size={28} className="spin" /> <span>{t('admin.cargando')}</span></div>
        )}
        {error && !loading && (
          <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="admin-empty">{t('profesor.reservar.vacio')}</p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="profesor-reservar-grid">
            {items.map((it) => {
              const key = `${it.horario_id}|${it.fecha_clase}`;
              const isReservando = reservandoKey === key;
              const sinPlazas = it.plazas_restantes <= 0;
              const tieneDescuento = it.descuento_pct > 0;
              return (
                <article key={key} className="profesor-reservar-card">
                  <div className="prc-fecha">
                    <Calendar size={14} strokeWidth={1.5} />
                    <span>{formatFechaLarga(it.fecha_clase, lang)} · {it.hora_inicio}–{it.hora_fin}</span>
                  </div>
                  <h3 className="prc-clase">{it.clase_tipo?.nombre || ''}</h3>
                  <div className="prc-meta">
                    {it.profesor_titular?.nombre_publico && (
                      <span className="prc-meta-item">{t('profesor.reservar.con')} {it.profesor_titular.nombre_publico}</span>
                    )}
                    {it.sala && (
                      <span className="prc-meta-item"><MapPin size={12} strokeWidth={1.5} /> {it.sala}</span>
                    )}
                    <span className="prc-meta-item">
                      <Users size={12} strokeWidth={1.5} />
                      {sinPlazas
                        ? t('profesor.reservar.sin_plazas')
                        : t('profesor.reservar.plazas', { count: it.plazas_restantes })}
                    </span>
                  </div>
                  {tieneDescuento ? (
                    <div className="tc-precio-prof">
                      <span className="tc-precio-publico">{Number(it.precio_publico).toFixed(2)} €</span>
                      <span className="tc-precio-prof-final">{Number(it.precio_profesor).toFixed(2)} €</span>
                      <span className="tc-precio-prof-pct">−{it.descuento_pct}%</span>
                    </div>
                  ) : (
                    <div className="prc-precio-simple">{Number(it.precio_publico).toFixed(2)} €</div>
                  )}
                  <button
                    type="button"
                    className="btn-mini btn-primary prc-reservar"
                    onClick={() => reservar(it)}
                    disabled={isReservando || sinPlazas}
                  >
                    {isReservando
                      ? <><Loader2 size={14} className="spin" /> {t('profesor.reservar.reservando')}</>
                      : sinPlazas
                        ? t('profesor.reservar.sin_plazas_btn')
                        : t('profesor.reservar.btn_reservar')}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
